import { getTabbables } from '@disy/lib/event-util';
import { startKeyboardNavigation } from '@disy/lib/setup-keynav';
import type { SelectionMode } from '@disy/ui.responsive-table/responsive-table';
import { SELECTION_CHECKBOX_CLASS, TableEvents } from '@disy/ui.responsive-table/responsive-table-constants';

const TABBABLE_DATA_ATTRIBUTE = 'data-is-tabbable';

export class ResponsiveTableKeyboardEventHandler {
  _table: HTMLTableElement;
  _selectionMode: SelectionMode;

  constructor(table: HTMLTableElement, selectionMode: SelectionMode) {
    this._table = table;
    this._selectionMode = selectionMode;
  }

  _turnAllTableDescendantsUntabbable() {
    // remove all tabbable elements from tab-order for the roving tabindex pattern (cf. https://www.w3.org/TR/wai-aria-practices-1.1/#kbd_roving_tabindex)
    getTabbables(this._table).forEach((tabbableElement) => {
      tabbableElement.setAttribute(TABBABLE_DATA_ATTRIBUTE, String(true));
      tabbableElement.tabIndex = -1;
    });
    // make first row tabbable
    const rowToBeTabbable: HTMLElement | null = this._table.querySelector('tr');
    if (rowToBeTabbable) {
      rowToBeTabbable.tabIndex = 0;
    }
  }

  handleEvent(event: Event) {
    switch (event.type) {
      case 'keydown':
        this._handleKeyboardEvent(event as KeyboardEvent);
        break;
      case TableEvents.REFRESH:
        this._turnAllTableDescendantsUntabbable();
        break;
    }
  }

  _handleKeyboardEvent(keyboardEvent: KeyboardEvent) {
    switch (keyboardEvent.key) {
      case 'ArrowUp':
        this._handleArrowUpAndDownKey(keyboardEvent, -1);
        break;
      case 'ArrowDown':
        this._handleArrowUpAndDownKey(keyboardEvent, +1);
        break;
      case 'ArrowRight':
        this._handleArrowRightAndLeftKey(keyboardEvent, +1);
        break;
      case 'ArrowLeft':
        this._handleArrowRightAndLeftKey(keyboardEvent, -1);
        break;
      case 'Tab':
        this._handleShiftTabKey(keyboardEvent);
        break;
      case ' ':
        this._handleSpaceKey(keyboardEvent);
        break;
    }
  }

  _handleShiftTabKey(event: KeyboardEvent) {
    // The focus must be on a row (and not on a contained element) on shift tab to ensure that the focus falls on an element outside the table.
    if (event.shiftKey) {
      (event.target as Element).closest('tr')?.focus();
    }
  }

  _handleArrowUpAndDownKey(keyboardEvent: KeyboardEvent, delta: number) {
    keyboardEvent.preventDefault();
    startKeyboardNavigation();
    const activeRow = this._getActiveRow(keyboardEvent);
    const neighboringRow = this._getNeighboringRow(activeRow, delta);
    const activeCell = this._getActiveCell(keyboardEvent);
    this._switchFocusToElementInOtherRow(activeRow, activeCell, neighboringRow);
  }

  _handleArrowRightAndLeftKey(keyboardEvent: KeyboardEvent, delta: number) {
    keyboardEvent.preventDefault();
    startKeyboardNavigation();
    const activeRow = this._getActiveRow(keyboardEvent);
    const focusableElements = this._getFocusableElementsInRow(activeRow);
    const focusedIndex = focusableElements.indexOf(keyboardEvent.target as HTMLElement);

    if (focusedIndex + delta >= 0) {
      focusableElements[focusedIndex + delta]?.focus();
    } else {
      activeRow.focus();
    }
  }

  _handleSpaceKey(event: KeyboardEvent) {
    if ((event.target as HTMLElement).matches('tr')) {
      event.preventDefault();
      const activeRow = this._getActiveRow(event);
      switch (this._selectionMode) {
        case 'multiple':
          this._toggleSelectionCheckbox(activeRow);
          break;
        case 'single':
          this._toggleRowSelection(activeRow);
          break;
      }
    }
  }

  _getActiveRow(keyboardEvent: KeyboardEvent): HTMLTableRowElement {
    return (keyboardEvent.target as HTMLElement).closest('tr') as HTMLTableRowElement;
  }

  _getActiveCell(keyboardEvent: KeyboardEvent) {
    return this._getEnclosingCellForElement(keyboardEvent.target as HTMLElement);
  }

  _getEnclosingCellForElement(element: Element): HTMLTableCellElement {
    return element.closest('td, th')!;
  }

  _getFocusableElementsInRow(row: HTMLTableRowElement): HTMLElement[] {
    return Array.from(row.querySelectorAll(`[${TABBABLE_DATA_ATTRIBUTE}]`));
  }

  _getNeighboringRow(row: HTMLTableRowElement, delta: number) {
    const rows = [...this._table.rows];
    return rows[rows.indexOf(row) + delta];
  }

  _toggleSelectionCheckbox(row: HTMLTableRowElement) {
    (row.querySelector(`.${SELECTION_CHECKBOX_CLASS}`) as HTMLElement)?.click();
  }

  _toggleRowSelection(row: HTMLTableRowElement) {
    row.click();
  }

  _switchFocusToElementInOtherRow(
    activeRow: HTMLTableRowElement,
    activeCell: HTMLTableCellElement,
    rowToBeFocused: HTMLTableRowElement | null,
  ) {
    if (!rowToBeFocused) {
      activeRow.focus();
      return;
    }

    const focusableElements = this._getFocusableElementsInRow(rowToBeFocused).filter(
      (element) => this._getEnclosingCellForElement(element)?.cellIndex <= activeCell?.cellIndex,
    );
    const nextElementToBeFocused = focusableElements[focusableElements.length - 1] ?? rowToBeFocused;
    activeRow.tabIndex = -1;
    rowToBeFocused.tabIndex = 0;
    nextElementToBeFocused.focus();
  }
}
